"use client"

import * as React from "react"
import { Input } from "@/components/ui/input"
import { useFormatters } from "@/hooks/use-formatters"
import { useNumericInput } from "@/hooks/use-numeric-input"

export interface PercentInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'readOnly'> {
  /** The numeric percentage value in 0-100 range (e.g., 25 for 25%, NOT 0.25) */
  value?: number | null
  /** Called when the numeric value changes (returns value in 0-100 range) */
  onChange?: (value: number | null) => void
  /** Whether to allow negative values */
  allowNegative?: boolean
  /** Whether the input is read-only */
  readOnly?: boolean
}

const PercentInput = React.forwardRef<HTMLInputElement, PercentInputProps>(
  ({ className, value, onChange, allowNegative = false, readOnly = false, ...props }, ref) => {
    const { formatPercent } = useFormatters()
    
    // Use shared numeric input logic
    const {
      displayValue,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyDown
    } = useNumericInput({
      value,
      decimalPlaces: 2,
      allowNegative,
      formatter: formatPercent
    })

    return (
      <Input
        type="text"
        className={className}
        ref={ref}
        value={displayValue}
        readOnly={readOnly}
        {...props}
        {...(readOnly ? {} : {
          onChange: handleChange,
          onFocus: (e) => handleFocus(e, value, props.onFocus),
          onBlur: (e) => handleBlur(e, displayValue, onChange, props.onBlur),
          onKeyDown: (e) => handleKeyDown(e, props.onKeyDown)
        })}
      />
    )
  }
)
PercentInput.displayName = "PercentInput"

export { PercentInput }

